<template>
  <div>
    <div class="d-flex justify-content-end align-items-center mb-1">
      <!--
        Rótulo delimita o alcance do seletor: ele muda a base das roscas por
        status e não toca em nenhum card vindo pelo slot `leading`.
      -->
      <small class="text-muted mr-50 text-nowrap">Gráficos por status</small>
      <b-button-group size="sm">
        <b-button
          :variant="calcMode === 'quantity' ? 'primary' : 'outline-primary'"
          @click="calcMode = 'quantity'"
        >
          Quantidade
        </b-button>
        <b-button
          :variant="calcMode === 'billing' ? 'primary' : 'outline-primary'"
          @click="calcMode = 'billing'"
        >
          Faturamento
        </b-button>
      </b-button-group>
    </div>
    <b-row class="match-height">
      <!--
        Cards que abrem a grade sem pertencer à distribuição por status (hoje, o
        churn). Ficam na MESMA b-row para herdar quebra de linha e altura casada,
        em vez de ocupar uma linha própria. Nada aqui é afetado por `calcMode`.
      -->
      <slot name="leading" />
      <b-col
        v-for="(item, index) in items"
        :key="`totalizer-${index}`"
        class="totalizer-card"
      >
        <b-card no-body class="earnings-card">
          <b-card-header>
            <h4 class="mb-0 mouse-pointer" @click="filterByChart(item.data)">
              {{ item.name }}
            </h4>
            <b-card-text class="mb-0 totalizer-help">
              <b-button
                :id="`help-totalizer-${index}`"
                variant="flat"
                class="btn-icon p-0"
              >
                <feather-icon
                  icon="HelpCircleIcon"
                  size="21"
                  class="text-muted cursor-pointer"
                />
              </b-button>
              <b-popover :target="`help-totalizer-${index}`" triggers="focus" placement="top">
                <template #title>{{ item.name }}</template>
                <p
                  v-for="status in getStatusesForItem(item.data)"
                  :key="status.id"
                  class="mb-25"
                >
                  <b>{{ status.name }}:</b> {{ status.description }}
                </p>
              </b-popover>
            </b-card-text>
          </b-card-header>
          <b-card-body class="mouse-pointer" @click="filterStatus(item.data)">
            <!-- apex chart -->
            <template v-if="processChart(item)">
              <div>
                <vue-apex-charts
                  height="130"
                  :options="item.options"
                  :series="item.series"
                />
              </div>
            </template>
            <div v-if="loading" class="position-absolute spinner-position">
              <b-spinner variant="primary" />
            </div>
          </b-card-body>
          <b-row class="text-center mx-0">
            <b-col
              cols="6"
              class="border-top border-right d-flex align-items-between flex-column py-1"
              style="padding-left: 1px; padding-right: 1px"
            >
              <b-card-text class="text-muted mb-0" style="font-size: smaller">
                Faturamento
              </b-card-text>
              <div class="font-weight-bolder mb-0">
                {{ item.total | toCurrency }}
              </div>
            </b-col>
            <b-col
              cols="6"
              class="border-top d-flex align-items-between flex-column py-1"
            >
              <b-card-text class="text-muted mb-0" style="font-size: smaller">
                Quantidade
              </b-card-text>
              <div class="font-weight-bolder mb-0">
                {{ item.quantity }}
              </div>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardText,
  BCardBody,
  BRow,
  BCol,
  BMedia,
  BMediaAside,
  BAvatar,
  BMediaBody,
  BSpinner,
  BPopover,
  BButton,
  BButtonGroup,
} from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";
import { mapGetters } from "vuex";
import * as types from "../store/types";
import { floatToMoney } from "@/helpers/converters";

const REMAINDER_COLOR_DARK = "#4b5a7e";
const REMAINDER_COLOR_LIGHT = "#e2e8f0";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardText,
    BCardBody,
    BMedia,
    BAvatar,
    BMediaAside,
    BMediaBody,
    VueApexCharts,
    BPopover,
    BButton,
    BButtonGroup,
    BSpinner,
  },
  props: {
    totalizers: {
      type: Array,
      default: () => [],
    },
    aggroupments: {
      type: Array,
      default: () => [],
    },
    filterStatus: Function,
    // Vem do pai para o spinner acompanhar a busca real da tela.
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      calcMode: "quantity",
    };
  },
  computed: {
    ...mapGetters({
      statusList: types.STATUS_LIST,
    }),
    items() {
      return this.aggroupments;
    },
    totalQuantity() {
      return this.totalizers.reduce((sum, t) => sum + (t.quantity || 0), 0);
    },
    totalBilling() {
      return this.totalizers.reduce((sum, t) => sum + (t.gross_value || 0), 0);
    },
    isDarkMode() {
      return this.$store.state.appConfig?.layout?.skin === "dark";
    },
  },
  methods: {
    // Título e corpo filtram igual. O título disparava busca própria só com
    // { status }, descartando os demais filtros e a paginação.
    filterByChart(id) {
      this.filterStatus(id);
    },
    processChart(item) {
      item.series = [];
      item.labels = [];
      item.slice_colors = [];
      item.total = 0;
      item.total_percentage = 0;
      item.quantity = 0;

      const base = this.calcMode === "quantity" ? this.totalQuantity : this.totalBilling;

      item.data.forEach((status_id, index) => {
        const val = this.totalizers.find(
          (e) => e.id_contract_status == status_id
        );
        if (val) {
          const value =
            this.calcMode === "quantity" ? val.quantity : val.gross_value;
          const pct = base > 0 ? (value / base) * 100 : 0;

          item.series.push(pct);
          item.slice_colors.push(item.colors[index]);
          item.labels.push(val.description);
          item.total += val.gross_value;
          item.total_percentage += pct;
          item.quantity += val.quantity;
        }
      });

      if (item.total_percentage > 100) {
        item.total_percentage = 100;
      }

      // Add a remainder slice so the arc visually represents the real percentage
      const remainder = parseFloat((100 - item.total_percentage).toFixed(4));
      const remainderColor = this.isDarkMode
        ? REMAINDER_COLOR_DARK
        : REMAINDER_COLOR_LIGHT;
      if (remainder > 0.005) {
        item.series.push(remainder);
        item.slice_colors.push(remainderColor);
        item.labels.push("");
      }

      const visibleSlicesCount = item.series.length - (remainder > 0.005 ? 1 : 0);
      const mode = this.calcMode;
      const itemTotal = item.total;
      const itemQuantity = item.quantity;
      const itemTotalPct = item.total_percentage;

      item.options = {
        chart: {
          type: "donut",
          toolbar: { show: false },
          width: "100%",
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        labels: item.labels,
        colors: item.slice_colors,
        tooltip: {
          x: { show: false },
          y: {
            formatter(value, { seriesIndex }) {
              if (seriesIndex >= visibleSlicesCount) return null;
              if (mode === "quantity") {
                return Math.round(itemQuantity * (value / itemTotalPct));
              }
              return floatToMoney(itemTotal * (value / itemTotalPct));
            },
          },
        },
        stroke: { width: 0 },
        grid: {
          padding: { right: -20, bottom: -8, left: -20 },
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                name: {
                  offsetY: 15,
                  formatter() {
                    return "Total";
                  },
                },
                value: {
                  offsetY: -15,
                  formatter(val) {
                    return `${parseFloat(parseFloat(val).toFixed(2))}%`;
                  },
                },
                total: {
                  show: true,
                  offsetY: 15,
                  label: "Total",
                  formatter() {
                    return parseFloat(itemTotalPct.toFixed(2)) + "%";
                  },
                },
              },
            },
          },
        },
      };

      return item.quantity > 0;
    },
    getStatusesForItem(list) {
      return this.statusList.filter((e) => list.includes(e.id.toString()));
    },
  },
};
</script>

<style lang="scss">
.totalizer-card {
  min-width: 250px;
  max-width: 500px !important;
  width: max-content !important;
}
.totalizer-help {
  position: absolute;
  top: 10px;
  right: 10px;
}

.mouse-pointer {
  cursor: pointer;
}

.spinner-position {
  bottom: 80px;
  right: 15px;
}
</style>
